import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import axios from 'axios';
import { Select, Spin, Modal, Tooltip } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../../providers/UserContext';
import { useTranslation } from 'react-i18next';

//import PrincipalStats from './PrincipalStatsCompare';
import NoReviewsCard from '../NoReviewsCard/NoReviewsCard';
import LoadingScreen from '../LoadingScreen/LoadingScreenCustom';
import './Compare.css';
import PremiumRequiredCard from '../PremiumRequiredCard/PremiumRequiredCard';

const { Option } = Select;

const Compare = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(); // Hook para traducciones
  const [nearbyBusinesses, setNearbyBusinesses] = useState([]);
  const [radius, setRadius] = useState(1);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [loadingComplete, setLoadingComplete] = useState(true);

  // Estados para las opciones de gráficos
  const [lineChartOptions, setLineChartOptions] = useState({});
  const [barChartOptions, setBarChartOptions] = useState({});
  const [radarChartOptions, setRadarChartOptions] = useState({});
  const [trendChartOptions, setTrendChartOptions] = useState({});
  const [activityChartOptions, setActivityChartOptions] = useState({});
  const [scatterChartOptions, setScatterChartOptions] = useState({});

  const [usuario, setUsuario]=useState(null);
  const [error, setError] = useState(false);
  const [isExpired, setIsExpired] = useState(true);

  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const isMobile = windowSize.width <= 1000;

  const { user } = useUser();
  const [myBusinessData, setMyBusinessData] = useState(user?.business_data || null);

  const apiUrl = process.env.REACT_APP_API_URL;

// 1. Primer useEffect: Carga de datos iniciales del negocio y del usuario
useEffect(() => {
  const fetchData = async () => {
    // var userUID = user.uid ? user.uid : localStorage.getItem("userUID")
    try {
      // const [businessResponse, userResponse] = await Promise.all([
      //   axios.get(`${apiUrl}/collections/todo_restaurantes_españa/${user.businessId}`),
      //   axios.get(`${apiUrl}/collections/user/${userUID}`)
      // ]);
      if (user) {
        setMyBusinessData(user?.business_data || null);
        setUsuario(user);
        //checkSubscriptionStatus(user);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(true);
    }
  };

  if (user && user.businessId) {
    fetchData();
  }
}, [user]);

// const checkSubscriptionStatus = (usuario) => {
//   const parseDate = (dateString) => {
//     const [day, month, year] = dateString.split('/').map(Number);
//     return new Date(year, month - 1, day); // Los meses en JavaScript son indexados desde 0
//   };

//   if (user && !user.is_premium) {
//     const registrationDate = parseDate(usuario.date);
//     const expirationDate = new Date(registrationDate);
//     expirationDate.setDate(expirationDate.getDate() + 7);
//     const currentDate = new Date();
//     setIsExpired(currentDate > expirationDate);
//   }
// };

// 2. Segundo useEffect: Carga de negocios cercanos cuando myBusinessData está listo o radius cambia
useEffect(() => {
  const fetchNearbyBusinesses = async () => {
    if (myBusinessData) {
      try {
        setDataLoaded(false); // Iniciar proceso de carga
        const location = myBusinessData.coordenadas;
        const response = await axios.get(`${apiUrl}/collections/nearby-businesses`, {
          params: {
            radius,
            lat: location[1],
            lng: location[0],
            collection: 'todo_restaurantes_españa'
          }
        });
        setNearbyBusinesses(response.data);
        setDataLoaded(true); // Marcar datos como cargados
      } catch (error) {
        console.error('Error fetching nearby businesses:', error);
        setError(true);
      }
    }
  };

  fetchNearbyBusinesses();
}, [radius, myBusinessData]);

// 3. Tercer useEffect: Procesamiento de datos para gráficos cuando los datos están listos
useEffect(() => {
  if (dataLoaded && myBusinessData && myBusinessData.allReviews && nearbyBusinesses.length > 0) {
    // Procesamiento de datos y actualización de gráficos
    const myRatingsOverTime = processReviews(myBusinessData.allReviews);
    const nearbyRatingsOverTime = processReviews(nearbyBusinesses.flatMap(b => b.allReviews));

    const myBusinessRatings = processReviewsRadar(myBusinessData.allReviews);
    const nearbyBusinessRatings = processReviewsRadar(nearbyBusinesses.flatMap(b => b.allReviews));

    const myActivityData = processActivityData(myBusinessData.allReviews);
    const nearbyActivityData = processActivityData(
      nearbyBusinesses.flatMap(b => b.allReviews),
      true,
      nearbyBusinesses.length || 1
    );

    const myTrendData = processTrendData(myBusinessData.allReviews);
    const nearbyTrendData = processTrendData(
      nearbyBusinesses.flatMap(b => b.allReviews),
      true,
      nearbyBusinesses.length || 1
    );

    // Organización de datos para gráficos
    const allMonths = Array.from(new Set([...myTrendData.map(d => d.month), ...nearbyTrendData.map(d => d.month)])).sort(
      (a, b) => new Date(a) - new Date(b)
    );

    const alignedMyTrendData = allMonths.map(month => {
      const data = myTrendData.find(d => d.month === month);
      return data ? data.count : 0;
    });

    const alignedNearbyTrendData = allMonths.map(month => {
      const data = nearbyTrendData.find(d => d.month === month);
      return data ? data.count : 0;
    });

    const currentDate = new Date();
    const startDate = new Date(currentDate.setFullYear(currentDate.getFullYear() - 2));

    const filteredAndSortedData = allMonths
      .map((month, index) => {
        const [shortMonth, year] = month.split(' ');
        const fullDate = new Date(`${shortMonth} 1, ${year}`);
        return {
          month: fullDate,
          myTrendData: alignedMyTrendData[index],
          nearbyTrendData: alignedNearbyTrendData[index]
        };
      })
      .filter(item => item.month >= startDate)
      .sort((a, b) => a.month - b.month);

    // Extraer meses y datos ordenados
    const filteredMonths = filteredAndSortedData.map(item =>
      item.month.toLocaleString(i18n.language, { month: 'short', year: 'numeric' })
    );
    const filteredMyTrendData = filteredAndSortedData.map(item => item.myTrendData);
    const filteredNearbyTrendData = filteredAndSortedData.map(item => item.nearbyTrendData);

      setLineChartOptions(createLineChartOptions(myRatingsOverTime, nearbyRatingsOverTime));
      setBarChartOptions(createBarChartOptions(myBusinessData, nearbyBusinesses));
      setRadarChartOptions(createRadarChartOptions(myBusinessRatings, nearbyBusinessRatings));
      setTrendChartOptions(createTrendChartOptions(filteredMonths, filteredMyTrendData, filteredNearbyTrendData));
      //setTrendChartOptions(createTrendChartOptions(allMonths, alignedMyTrendData, alignedNearbyTrendData));
      setActivityChartOptions(createActivityChartOptions(myActivityData, nearbyActivityData));

      let myBusinessScatterData;
      const scatterData = nearbyBusinesses.map(business => {
        const reviewsCount = business.numreseñas || 0;
        const valoracion = business.valoracion !== null ? business.valoracion : 0;

        if (business.id_negocio === myBusinessData.id_negocio) {
          myBusinessScatterData = [{
            value: [reviewsCount, valoracion],
            name: business.nombre,
            tooltip: {
              formatter: `<strong>${business.nombre} (${t('yourBusiness')})</strong><br>${t('reviews')}: ${reviewsCount}<br>${t('rating')}: ${valoracion}`
            }
          }];
          return null;
        }

        return {
          value: [reviewsCount, valoracion],
          name: business.nombre,
          tooltip: {
            formatter: `<strong>${business.nombre}</strong><br>${t('reviews')}: ${reviewsCount}<br>${t('rating')}: ${valoracion}`
          }
        };
      }).filter(Boolean);

      setScatterChartOptions({
        tooltip: {
          trigger: 'item',
          formatter: function (params) {
            return params.data.tooltip.formatter;
          },
          textStyle: {
            fontSize: isMobile ? 12 : 14
          },
          position: isMobile ? function (point) {
            const x = point[0] + 10;
            const y = point[1] - 30;
            return [x, y];
          } : null,
        },
        xAxis: {
          type: 'value',
          name: t('numberOfReviews'),
          min: 0,
          nameTextStyle: {
            align: isMobile ? "right" : "left",
            verticalAlign: isMobile ? "bottom" : "middle",
            lineHeight: -90
          },
          axisLabel: {
            rotate: isMobile ? 50 : 0
          }
        },
        yAxis: {
          type: 'value',
          name: t('averageRating'),
          min: 0,
          max: 5,
          nameTextStyle: {
            align: isMobile ? "left" : "center"
          }
        },
        series: [
          {
            name: t('nearbyBusiness'),
            type: 'scatter',
            data: scatterData,
            symbolSize: 10,
            z: 1,
            itemStyle: {
              color: '#273C5A' // Azul para "negocios cercanos"
            },
            emphasis: {
              itemStyle: {
                borderColor: '#000',
                borderWidth: 2
              }
            }
          },
          {
            name: t('myBusiness'),
            type: 'scatter',
            data: myBusinessScatterData,
            symbolSize: 15,
            itemStyle: { 
              color: '#EFC94C' // Amarillo para "mi negocio"
            },
            z: 10,
            emphasis: {
              itemStyle: {
                borderColor: '#000',
                borderWidth: 2
              }
            }
          }
        ]
      });
      

    setLoadingComplete(true);
  } else {
    const timeout = setTimeout(() => {
      setLoadingComplete(true);
      }, 1000);

    return () => clearTimeout(timeout);
  }
}, [dataLoaded, myBusinessData, nearbyBusinesses, i18n.language]);

  const processReviews = (reviews) => {
    const ratingsOverTime = { months: [], oneStar: [], twoStars: [], threeStars: [], fourStars: [], fiveStars: [] };
    const reviewsByMonth = {};

    reviews.forEach(review => {
      if (review !== null && review.rating) {
        const month = new Date(review.date).toLocaleString(i18n.language, { month: 'short', year: 'numeric' });
        if (!reviewsByMonth[month]) {
          reviewsByMonth[month] = { oneStar: 0, twoStars: 0, threeStars: 0, fourStars: 0, fiveStars: 0 };
        }
        switch (review.rating) {
          case 1: reviewsByMonth[month].oneStar++; break;
          case 2: reviewsByMonth[month].twoStars++; break;
          case 3: reviewsByMonth[month].threeStars++; break;
          case 4: reviewsByMonth[month].fourStars++; break;
          case 5: reviewsByMonth[month].fiveStars++; break;
          default: break;
        }
      }
    });

    Object.keys(reviewsByMonth).forEach(month => {
      ratingsOverTime.months.push(month);
      ratingsOverTime.oneStar.push(reviewsByMonth[month].oneStar);
      ratingsOverTime.twoStars.push(reviewsByMonth[month].twoStars);
      ratingsOverTime.threeStars.push(reviewsByMonth[month].threeStars);
      ratingsOverTime.fourStars.push(reviewsByMonth[month].fourStars);
      ratingsOverTime.fiveStars.push(reviewsByMonth[month].fiveStars);
    });

    return ratingsOverTime;
  };

  // PROCESS DATA PARA EL RADAR CHART
  const processReviewsRadar = (reviews) => {
    const validReviews = reviews.filter(review => review !== null && review.rating !== null);
    const length = validReviews.length;

    const ratingCounts = [0, 0, 0, 0, 0];

    validReviews.forEach(review => {
      if (review.rating >= 1 && review.rating <= 5) {
        ratingCounts[review.rating - 1]++;
      }
    });

    const valoracion = validReviews.reduce((acc, review) => acc + review.rating, 0) / length;

    return {
      valoracion,
      length,
      oneStar: (ratingCounts[0] / length) * 100,
      twoStars: (ratingCounts[1] / length) * 100,
      threeStars: (ratingCounts[2] / length) * 100,
      fourStars: (ratingCounts[3] / length) * 100,
      fiveStars: (ratingCounts[4] / length) * 100
    };
  };

  const processTrendData = (reviews, isAverage = false, totalBusinesses = 1) => {
    const trendData = {};

    // Iterar sobre las reseñas y contar las ocurrencias por mes
    reviews.forEach(review => {
      if (review !== null && review.date !== null) {
        const date = new Date(review.date);
        if (!isNaN(date.getTime())) { // Verifica que la fecha sea válida
          const month = date.toLocaleString('default', { month: 'short', year: 'numeric' });
          if (!trendData[month]) {
            trendData[month] = 0;
          }
          trendData[month]++;
        }
      }
    });

    // Obtener todos los meses posibles y asegurar que están en trendData
    const months = [...new Set(reviews.map(review => {
      if (review !== null && review.date !== null) {
        const date = new Date(review.date);
        if (!isNaN(date.getTime())) {
          return date.toLocaleString('default', { month: 'short', year: 'numeric' });
        }
      }
      return null;
    }).filter(month => month !== null))];

    months.forEach(month => {
      if (!trendData[month]) {
        trendData[month] = 0;
      }
    });

    return Object.entries(trendData).map(([month, count]) => ({
      month,
      count: isAverage ? Math.round(count / totalBusinesses) : count // Redondeo de valores promediados
    }));
  };


  const processActivityData = (reviews, isAverage = false, totalBusinesses = 1) => {
    const activityData = new Array(24).fill(0);

    reviews.forEach(review => {
      if (review !== null && review.date !== null) {
        const date = new Date(review.date);
        if (!isNaN(date.getTime())) {  // Verifica que la fecha sea válida
          const hour = date.getHours();
          activityData[hour]++;
        }
      }
    });

    return isAverage ? activityData.map(count => Math.round(count / totalBusinesses)) : activityData;
  };

  // Encapsulando la configuración de los gráficos
  const createLineChartOptions = (myRatingsOverTime, nearbyRatingsOverTime) => ({
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' },
      textStyle: {
        fontSize: isMobile ? 12 : 14
      },
      position: isMobile ? function (point) {
        const x = point[0] + 10;
        const y = point[1] - 30;
        return [x, y];
      } : null,
    },
    legend: {
      bottom: 0,
      left: 'center',
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { fontSize: 12 },
      padding: [5, 0],
      orient: 'horizontal',
      height: '70%',
      scroll: true,
      data: [`1★ (${t('myBusiness')})`, `1★ (${t('nearbyBusiness')})`, `2★ (${t('myBusiness')})`, `2★ (${t('nearbyBusiness')})`, `3★ (${t('myBusiness')})`, `3★ (${t('nearbyBusiness')})`,
      `4★ (${t('myBusiness')})`, `4★ (${t('nearbyBusiness')})`, `5★ (${t('myBusiness')})`, `5★ (${t('nearbyBusiness')})`]
    },
    xAxis: {
      type: 'category',
      data: myRatingsOverTime.months,
      axisLine: { show: true },
      axisLabel: { show: true },
      axisTick: { show: false },
      splitLine: { show: false }
    },
    yAxis: {
      type: 'value',
      name: t("numberOfReviews"),
      nameTextStyle: {
        align: isMobile ? "left" : "center"
      },
      axisLine: { show: true },
      axisLabel: { show: true },
      axisTick: { show: false },
      splitLine: { show: false }
    },
    grid: {
      bottom: isMobile ? '35%' : '20%'
    },
    series: [
      { name: `1★ (${t('myBusiness')})`, type: 'bar', data: myRatingsOverTime.oneStar, itemStyle: { color: '#8e44ad' } },
      { name: `1★ (${t('nearbyBusiness')})`, type: 'bar', data: nearbyRatingsOverTime.oneStar, itemStyle: { color: '#d4b7e0' } },
      { name: `2★ (${t('myBusiness')})`, type: 'bar', data: myRatingsOverTime.twoStars, itemStyle: { color: '#e74c3c' } },
      { name: `2★ (${t('nearbyBusiness')})`, type: 'bar', data: nearbyRatingsOverTime.twoStars, itemStyle: { color: '#e47e74' } },
      { name: `3★ (${t('myBusiness')})`, type: 'bar', data: myRatingsOverTime.threeStars, itemStyle: { color: '#f39c12' } },
      { name: `3★ (${t('nearbyBusiness')})`, type: 'bar', data: nearbyRatingsOverTime.threeStars, itemStyle: { color: '#f2c987' } },
      { name: `4★ (${t('myBusiness')})`, type: 'bar', data: myRatingsOverTime.fourStars, itemStyle: { color: '#2ecc71' } },
      { name: `4★ (${t('nearbyBusiness')})`, type: 'bar', data: nearbyRatingsOverTime.fourStars, itemStyle: { color: '#7af2ad' } },
      { name: `5★ (${t('myBusiness')})`, type: 'bar', data: myRatingsOverTime.fiveStars, itemStyle: { color: '#3498db' } },
      { name: `5★ (${t('nearbyBusiness')})`, type: 'bar', data: nearbyRatingsOverTime.fiveStars, itemStyle: { color: '#79c1f1' } }
    ]
  });

  const createBarChartOptions = (myBusinessData, nearbyBusinesses) => ({
    grid: {
      left: 0,
      right: 0,
      top: 6,
      bottom: 15,
      containLabel: true
    },
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' },
      textStyle: {
        fontSize: isMobile ? 12 : 14
      },
      position: isMobile ? function (point) {
        const x = point[0] + 10;
        const y = point[1] - 30;
        return [x, y];
      } : null,
    },
    legend: { data: [t('myBusiness'), t('nearbyBusiness')], bottom: 0, left: 'center' },
    xAxis: {
      type: 'category',
      data: [''] // Añadir etiquetas para el eje x
    },
    yAxis: { type: 'value', minInterval: 1 },
    series: [
      {
        name: t('myBusiness'),
        type: 'bar',
        symbol: 'circle',
        barWidth: '30%',
        data: [myBusinessData ? myBusinessData.valoracion : 0],
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {offset: 0, color: '#EFC94C'},
              {offset: 0.7, color: '#EFC94C'},
              {offset: 1, color: '#F7DA89'}
          ])
        },
        emphasis: {
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              {offset: 0, color: '#EFC94C'},
              {offset: 0.7, color: '#EFC94C'},
              {offset: 1, color: '#F7DA89'}
            ])
          }
        }
      },
      {
        name: t('nearbyBusiness'),
        type: 'bar',
        symbol: 'circle',
        barWidth: '30%',
        data: [nearbyBusinesses.length > 0 ? (nearbyBusinesses.reduce((sum, business) => sum + (business.valoracion || 0), 0) / nearbyBusinesses.length).toFixed(1) : 0],
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: '#273C5A' },
            { offset: 0.5, color: '#273C5A' },
            { offset: 1, color: '#273C5A' }
          ])
        },
        emphasis: {
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: '#273C5A' },
              { offset: 0.7, color: '#36a2eb' },
              { offset: 1, color: '#273C5A' }
            ])
          }
        }
      }
    ]
  });

  const createRadarChartOptions = (myBusinessRatings, nearbyBusinessRatings) => {

    const maxReviews = Math.max(myBusinessRatings.length, nearbyBusinessRatings.length);
    const maxOneStar = Math.max(myBusinessRatings.oneStar, nearbyBusinessRatings.oneStar);
    const maxTwoStars = Math.max(myBusinessRatings.twoStars, nearbyBusinessRatings.twoStars);
    const maxThreeStars = Math.max(myBusinessRatings.threeStars, nearbyBusinessRatings.threeStars);
    const maxFourStars = Math.max(myBusinessRatings.fourStars, nearbyBusinessRatings.fourStars);
    const maxFiveStars = Math.max(myBusinessRatings.fiveStars, nearbyBusinessRatings.fiveStars);

    return {
      tooltip: {
        textStyle: {
          fontSize: isMobile ? 12 : 14
        },
        position: isMobile ? function (point) {
          const x = point[0] + 10;
          const y = point[1] - 30;
          return [x, y];
        } : null,
      },
      legend: {
        data: [
          t('myBusiness'),
          t('nearbyBusiness')
        ],
        bottom: 0,
        left: 'center',
        textStyle: {
          fontSize: isMobile ? 12 : 14,
          color: '#000' // Asegúrate de que el texto sea visible
        },
      },
      radar: {
        indicator: [
          { name: t('averageRating'), max: 5 },
          { name: isMobile ? t('reviews') : t('numberOfReviews'), max: maxReviews },
          { name: '1★', max: maxOneStar },
          { name: '2★', max: maxTwoStars },
          { name: '3★', max: maxThreeStars },
          { name: '4★', max: maxFourStars },
          { name: '5★', max: maxFiveStars }
        ],
        radius: '65%',
        center: ['50%', '45%'],
      },
      series: [{
        name: 'Comparación de Negocios',
        type: 'radar',
        data: [
          {
            value: [
              myBusinessRatings.valoracion.toFixed(1),
              myBusinessRatings.length,
              myBusinessRatings.oneStar.toFixed(),
              myBusinessRatings.twoStars.toFixed(),
              myBusinessRatings.threeStars.toFixed(),
              myBusinessRatings.fourStars.toFixed(),
              myBusinessRatings.fiveStars.toFixed()
            ],
            name: t('myBusiness'),
            areaStyle: { color: '#EFC94C' },
            itemStyle: { // Color del símbolo en la leyenda
              color: '#EFC94C'
            }
          },
          {
            value: [
              nearbyBusinessRatings.valoracion.toFixed(1),
              nearbyBusinessRatings.length,
              nearbyBusinessRatings.oneStar.toFixed(),
              nearbyBusinessRatings.twoStars.toFixed(),
              nearbyBusinessRatings.threeStars.toFixed(),
              nearbyBusinessRatings.fourStars.toFixed(),
              nearbyBusinessRatings.fiveStars.toFixed()
            ],
            name: t('nearbyBusiness'),
            areaStyle: { color: '#273C5A' },
            itemStyle: { // Color del símbolo en la leyenda
              color: '#273C5A'
            }
          }
        ]
      }]
    };
    
    
  };

  const createTrendChartOptions = (allMonths, alignedMyTrendData, alignedNearbyTrendData) => ({
    tooltip: {
      trigger: 'axis',
      textStyle: {
        fontSize: isMobile ? 12 : 14
      },
      position: isMobile ? function (point) {
        const x = point[0] + 10;
        const y = point[1] - 30;
        return [x, y];
      } : null,
    },
    legend: {
      data: [t('myBusiness'), t('nearbyBusinessAverage')],
      bottom: 0,
      textStyle: {
        fontSize: isMobile ? 12 : 14,
      },
    },
    xAxis: {
      type: 'category',
      data: allMonths,
    },
    yAxis: {
      type: 'value',
      name: t('numberOfReviews'),
      nameTextStyle: {
        align: isMobile ? "left" : "center"
      }
    },
    series: [
      {
        name: t('myBusiness'),
        type: 'line',
        data: alignedMyTrendData,
        smooth: true,
        lineStyle: {
          color: '#EFC94C', // Línea amarilla
        },
        itemStyle: {
          color: '#EFC94C', // Puntos amarillos
        },
      },
      {
        name: t('nearbyBusinessAverage'),
        type: 'line',
        data: alignedNearbyTrendData,
        smooth: true,
        lineStyle: {
          color: '#273C5A', // Línea azul
        },
        itemStyle: {
          color: '#273C5A', // Puntos azules
        },
      }
    ]
  });
  

  const createActivityChartOptions = (myActivityData, nearbyActivityData) => ({
    tooltip: {
      trigger: 'axis',
      textStyle: {
        fontSize: isMobile ? 12 : 14
      },
      position: isMobile ? function (point) {
        const x = point[0] + 10;
        const y = point[1] - 30;
        return [x, y];
      } : null,
    },
    legend: {
      data: [t('myBusiness'), t('nearbyBusinessAverage')],
      bottom: 0,
      textStyle: {
        fontSize: isMobile ? 12 : 14,
      },
    },
    xAxis: {
      type: 'category',
      data: Array.from({ length: 24 }, (_, i) => `${i}:00`),
    },
    yAxis: {
      type: 'value',
      name: t('numberOfReviews'),
      nameTextStyle: {
        align: isMobile ? "left" : "center"
      }
    },
    series: [
      {
        name: t('myBusiness'),
        type: 'bar',
        data: myActivityData,
        itemStyle: {
          color: '#EFC94C', // Barras amarillas
        },
      },
      {
        name: t('nearbyBusinessAverage'),
        type: 'bar',
        data: nearbyActivityData,
        itemStyle: {
          color: '#273C5A', // Barras azules
        },
      }
    ]
  });
  
  const createScatterChartOptions = (scatterData, t) => ({
    tooltip: {
      trigger: 'item',
      formatter: (params) => params.data.tooltip.formatter,
    },
    xAxis: { 
      type: 'value', 
      name: t('numberOfReviews'), 
      min: 0 
    },
    yAxis: { 
      type: 'value', 
      name: t('averageRating'), 
      min: 0, 
      max: 5 
    },
    series: [{
      name: t('nearbyBusiness'),
      type: 'scatter',
      data: scatterData,
      symbolSize: (data) => data.symbolSize,
      itemStyle: {
        color: (params) => params.data.itemStyle.color,
      },
      emphasis: {
        itemStyle: { 
          borderColor: '#000', 
          borderWidth: 2 
        },
      },
    }]
  });

  const handleRadiusChange = (value) => {
    setRadius(value); // Mantiene el valor numérico
  };

  if (!loadingComplete && !myBusinessData) {
    return <LoadingScreen loading={!loadingComplete}/>;
  }
  
  if (loadingComplete && user && typeof user.is_premium !== 'undefined' && !user.is_premium && user.is_expired) {
    return <PremiumRequiredCard />;
  }

  if (user && !user.is_expired && (!myBusinessData || !myBusinessData.allReviews || (Array.isArray(myBusinessData.allReviews) && myBusinessData.allReviews.length === 0))) {
    return <NoReviewsCard />;
  }

  return (
    <div className="content pt-0 compare">
      <div className='footer-margin'>
      {/* Dropdown para elegir radio */}

      <div className="radius-section">
        <label className="radius-label">{t('comparisonRadius')}</label>
        <Select
          defaultValue="1"
          style={{ width: 120 }}
          onChange={handleRadiusChange}
          className='selectRadius'
          value={radius}
        >
          {[1, 2, 5, 10, 20, 30, 40, 50, 100, 200].map(radius => (
            <Option key={radius} value={radius}>
              {radius} km
            </Option>
          ))}
        </Select>
      </div>
      {/*nearbyBusinesses && <PrincipalStats data={nearbyBusinesses} />*/}
      <div className='row-container'>
        <div className='row'>
          <div className="col-xl-6">
            <div className="card">
              <div className="card-header d-flex py-0">
                <div className='card-title'>
                  <h6 className="pt-3 mb-0">{t('averageRatingsCompare')}
                    <Tooltip title={t('averageRatingsTooltipDescription')}>
                      <i className='ph-question ms-2 cursor-pointer info-icon'></i>
                    </Tooltip>
                  </h6>
                  <p>{t('averageRatingsCompareBody')}</p>
                </div>
              </div>
              <div className="card-body">
                <div className="chart-container">
                  {dataLoaded ? (
                    <>
                      <ReactECharts
                        option={barChartOptions}
                        id="bar_chart"
                        className="chart"
                        style={{ height: '300px'}}
                      />
                    </>
                  ) : (
                    <div className='spin-wrapper'>
                      <Spin />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-6">
            <div className="card">
              <div className="card-header d-flex py-0">
                <div className='card-title'>
                  <h6 className="pt-3 mb-0">{t('compareRatings')}                   
                    <Tooltip title={t('compareRatingsTooltipDescription')}>
                      <i className='ph-question ms-2 cursor-pointer info-icon'></i>
                    </Tooltip>
                  </h6>
                  <p>{t('compareRatingsBody')}</p>
                </div>
              </div>
              <div className="card-body">
                <div className="chart-container">
                  {dataLoaded ? (
                    <>
                      <ReactECharts
                        option={radarChartOptions}
                        id="bar_chart"
                        className="chart"
                        style={{ height: '300px'}}
                      />
                    </>
                  ) : (
                    <div className='spin-wrapper'>
                      <Spin />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header d-flex py-0" style={{filter: user && !user.is_premium ? 'blur(5px)' : 'none'}}>
              <div className='card-title'>
                <h6 className="pt-3 mb-0">{t('popularityRatings')}
                  <Tooltip title={t('popularityRatingsTooltipDescription')}>
                    <i className='ph-question ms-2 cursor-pointer info-icon'></i>
                  </Tooltip>
                </h6>
                <p>{t('popularityRatingsBody')}</p>
              </div>
            </div>
            <div className="card-body">
              <div className="chart-container">
                {dataLoaded ? (
                  <>
                  <ReactECharts
                    option={trendChartOptions}
                    style={{ height: '400px', filter: user && !user.is_premium ? 'blur(5px)' : 'none' }}
                  />
                  {user && !user.is_premium && (
                    <div className="premium-overlay">
                      <p>{t('premiumContent')}</p>
                      <Link to="/plans" className="btn-upgrade btnPremium">
                          {t('compareBlurButton')}
                          <i className="ph-arrow-circle-right ms-2"></i>
                      </Link>
                    </div>
                  )}
                  </>
                ) : (
                  <div className='spin-wrapper'>
                   <Spin />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header d-flex py-0" style={{filter: user && !user.is_premium ? 'blur(10px)' : 'none'}}>
              <div className='card-title'>
                <h6 className="pt-3 mb-0">{t('activityRatings')}
                  <Tooltip title={t('activityRatingsTooltipDescription')}>
                    <i className='ph-question ms-2 cursor-pointer info-icon'></i>
                  </Tooltip>
                </h6>
                <p>{t('activityRatingsBody')}</p>
              </div>
            </div>
            <div className="card-body">
              <div className="chart-container">
                {dataLoaded ? (
                  <>
                  <ReactECharts
                    option={activityChartOptions}
                    style={{ height: '400px', filter: user && !user.is_premium ? 'blur(10px)' : 'none' }}
                  />
                  {user && !user.is_premium && (
                    <div className="premium-overlay">
                      <p>{t('premiumContent')}</p>
                      <Link to="/plans" className="btn-upgrade btnPremium">
                          {t('compareBlurButton')}
                          <i className="ph-arrow-circle-right ms-2"></i>
                      </Link>
                    </div>
                  )}
                  </>
                ) : (
                  <div className='spin-wrapper'>
                    <Spin />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gráfico de Dispersión */}
      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header d-flex py-0" style={{filter: user && !user.is_premium ? 'blur(10px)' : 'none'}}>
              <div className='card-title'>
                <h6 className="pt-3 mb-0">{t('scatterChartTitle')}
                  <Tooltip title={t('scatterChartTooltipDescription')}>
                    <i className='ph-question ms-2 cursor-pointer info-icon'></i>
                  </Tooltip>
                </h6>
                <p>{t('scatterChartDescription')}</p>
              </div>
            </div>
            <div className="card-body">
              <div className="chart-container">
                {dataLoaded ? (
                  <>
                    <ReactECharts 
                      option={scatterChartOptions} 
                      style={{ height: '400px', filter: user && !user.is_premium ? 'blur(10px)' : 'none' }} 
                    />
                    {user && !user.is_premium && (
                      <div className="premium-overlay">
                        <p>{t('premiumContent')}</p>
                        <Link to="/plans" className="btn-upgrade btnPremium">
                          {t('compareBlurButton')}
                          <i className="ph-arrow-circle-right ms-2"></i>
                        </Link>
                      </div>
                    )}
                  </>
                ) : (
                  <div className='spin-wrapper'>
                    <Spin />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      
      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header d-flex py-0" style={{filter: usuario && !usuario.is_premium ? 'blur(5px)' : 'none'}}>
              <div className='card-title'>
                <h6 className="pt-3 mb-0">{t('timeRatings')}
                  <Tooltip title={t('timeRatingsTooltipDescription')}>
                    <i className='ph-question ms-2 cursor-pointer info-icon'></i>
                  </Tooltip>
                </h6>
                <p>{t('timeRatingsBody')}</p>
              </div>
            </div>

            {/* Contenedor del gráfico */}
            <div className="chart-container">
              {dataLoaded ? (
                <>
                  <ReactECharts
                    option={lineChartOptions}
                    style={{ height: '400px', filter: usuario && !usuario.is_premium ? 'blur(5px)' : 'none'}} 
                  />
                  {usuario && !usuario.is_premium && (
                    <div className="premium-overlay">
                      <p>{t('premiumContent')}</p>
                      <Link to="/plans" className="btn-upgrade btnPremium">
                          {t('compareBlurButton')}
                          <i className="ph-arrow-circle-right ms-2"></i>
                      </Link>
                    </div>
                  )}
                </>
              ) : (
                <div className='spin-wrapper'>
                  <Spin />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>


      {/* MODAL */}
      <Modal
        title={modalTitle}
        open={modalVisible}
        onOk={() => setModalVisible(false)}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        <div dangerouslySetInnerHTML={{ __html: modalContent }} />
      </Modal>
    </div>
    </div>
  );
};

export default Compare;
